
import { supabase } from '@/integrations/supabase/client';
import { UserType } from '@/types/user';
import { logWithTimestamp } from './authUtils';

/**
 * Gets the list of valid user types from the Supabase server
 * @returns Promise with the list of valid user types
 */
export async function getValidUserTypes(): Promise<string[]> {
  try {
    logWithTimestamp('Fetching valid user types from server');
    
    const { data, error } = await supabase.functions.invoke('get-valid-user-types');
    
    if (error) {
      logWithTimestamp('Error fetching valid user types:', error);
      // Default to types defined in our code
      return ['admin', 'free', 'starter', 'pro', 'premium', 'standard', 'analyst'];
    }
    
    if (data && data.validUserTypes && Array.isArray(data.validUserTypes)) {
      logWithTimestamp('Valid user types retrieved:', data.validUserTypes);
      return data.validUserTypes;
    }
    
    // Fallback to hard-coded list (must match database constraint)
    logWithTimestamp('Using fallback user type list');
    return ['admin', 'free', 'starter', 'pro', 'premium', 'standard', 'analyst'];
  } catch (error) {
    logWithTimestamp('Exception fetching valid user types:', error);
    // Default to types defined in our code as fallback
    return ['admin', 'free', 'starter', 'pro', 'premium', 'standard', 'analyst'];
  }
}

/**
 * Validates a user type against the list of valid types
 * @param userType Type to validate
 * @returns Promise resolving to boolean indicating validity
 */
export async function isValidUserType(userType: string): Promise<boolean> {
  if (!userType) return false;
  
  try {
    const validTypes = await getValidUserTypes();
    return validTypes.includes(userType);
  } catch (error) {
    logWithTimestamp('Error validating user type:', error);
    return false;
  }
}

/**
 * Normalizes a user type to ensure it's valid
 * @param userType Type to normalize
 * @param defaultType Default type to use if invalid
 * @returns Promise resolving to normalized user type
 */
export async function normalizeUserType(userType: string, defaultType: string = 'standard'): Promise<string> {
  if (!userType) return defaultType;
  
  try {
    const validTypes = await getValidUserTypes();
    
    // Force lowercase to handle case inconsistencies
    const normalizedType = userType.toLowerCase();
    
    // If the user type is valid, return it
    if (validTypes.includes(normalizedType)) {
      return normalizedType;
    }
    
    // Map aliases to standard types if necessary
    if (normalizedType === 'pay-per-report' || normalizedType === 'pay_per_report') {
      return 'starter';
    }
    
    logWithTimestamp(`Invalid user type "${userType}" normalized to "${defaultType}"`);
    return defaultType;
  } catch (error) {
    logWithTimestamp('Error normalizing user type:', error);
    return defaultType;
  }
}
